import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { useSimplifiedAuth } from '../../hooks/useSimplifiedAuth';

// Location capture contexts for different use cases
type LocationContext = 'auth_completion' | 'dashboard_onboarding' | 'satellite_view' | 'launch_view' | 'weather_alert';

interface LocationData {
  latitude: number;
  longitude: number;
  accuracy: number;
  city?: string;
  state?: string;
  country?: string;
  timezone?: string;
  method: 'gps_high' | 'gps_low' | 'network' | 'search' | 'manual' | 'ip_fallback';
  timestamp: string;
}

interface SmartLocationCaptureProps {
  context: LocationContext;
  onLocationCaptured?: (location: LocationData) => void;
  onSkip?: () => void;
  showSkipOption?: boolean;
  autoAttempt?: boolean;
  title?: string;
  subtitle?: string;
}

type CapturePhase = 'idle' | 'detecting' | 'permission_request' | 'searching' | 'manual' | 'success' | 'error';

// Animations
const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
`;

const slideIn = keyframes`
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const locationIcon = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
`;

// Styled Components
const Container = styled.div<{ $variant: LocationContext }>`
  padding: ${props => props.$variant === 'auth_completion' ? '24px' : '20px'};
  background: ${props => 
    props.$variant === 'auth_completion' 
      ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%)'
      : 'rgba(255, 255, 255, 0.05)'
  };
  border: 2px solid rgba(139, 92, 246, 0.3);
  border-radius: 16px;
  text-align: center;
  animation: ${slideIn} 0.4s ease-out;
  position: relative;
  overflow: hidden;
`;

const LocationIcon = styled.div<{ $detecting?: boolean }>`
  font-size: 2.5rem;
  margin-bottom: 16px;
  animation: ${props => props.$detecting ? pulse : locationIcon} 2s ease-in-out infinite;
`;

const Title = styled.h3`
  color: white;
  font-size: 1.3rem;
  font-weight: 600;
  margin: 0 0 8px 0;
`;

const Subtitle = styled.p`
  color: #94a3b8;
  font-size: 1rem;
  margin: 0 0 20px 0;
  line-height: 1.4;
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  margin: 0 8px 8px 0;
  
  ${props => props.$variant === 'primary' ? `
    background: linear-gradient(135deg, #8b5cf6, #6366f1);
    color: white;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(139, 92, 246, 0.4);
    }
  ` : `
    background: rgba(255, 255, 255, 0.1);
    color: #e2e8f0;
    border: 1px solid rgba(255, 255, 255, 0.2);
    
    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }
  `}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const DetectingState = styled.div`
  color: #94a3b8;
  font-size: 0.9rem;
  margin-top: 16px;
`;

const ErrorMessage = styled.div`
  color: #f87171;
  font-size: 0.9rem;
  margin-top: 12px;
  padding: 12px;
  background: rgba(248, 113, 113, 0.1);
  border-radius: 8px;
  border: 1px solid rgba(248, 113, 113, 0.3);
`;

const SuccessState = styled.div`
  color: #10b981;
  font-size: 1rem;
  margin-top: 16px;
  
  .location-details {
    font-size: 0.9rem;
    margin-top: 8px;
    color: #94a3b8;
  }
`;

const PrivacyNote = styled.div`
  font-size: 0.8rem;
  color: #64748b;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

// Context-specific messages
const CONTEXT_CONFIG = {
  auth_completion: {
    icon: '🌍',
    title: 'Complete Your Space Setup',
    subtitle: 'Get personalized satellite predictions and launch alerts for your location',
    primaryAction: 'Share My Location',
    secondaryAction: 'Set Manually',
    skipAction: 'Skip for Now'
  },
  dashboard_onboarding: {
    icon: '📍',
    title: 'Enhance Your Mission Control',
    subtitle: 'Add your location to see which satellites will be visible tonight',
    primaryAction: 'Enable Location',
    secondaryAction: 'Enter City',
    skipAction: 'Maybe Later'
  },
  satellite_view: {
    icon: '🛰️',
    title: 'Get Precise Predictions',
    subtitle: 'Share your location to see exactly when satellites will pass over',
    primaryAction: 'Share Location',
    secondaryAction: 'Search City',
    skipAction: 'Use Default'
  },
  launch_view: {
    icon: '🚀',
    title: 'Will You See This Launch?',
    subtitle: 'Check if this launch will be visible from your location',
    primaryAction: 'Check Visibility',
    secondaryAction: 'Enter Location',
    skipAction: 'Skip'
  },
  weather_alert: {
    icon: '🌤️',
    title: 'Get Weather Alerts',
    subtitle: 'Receive notifications about optimal stargazing conditions',
    primaryAction: 'Enable Alerts',
    secondaryAction: 'Set Location',
    skipAction: 'No Thanks'
  }
};

const SmartLocationCapture: React.FC<SmartLocationCaptureProps> = ({
  context,
  onLocationCaptured,
  onSkip,
  showSkipOption = true,
  autoAttempt = false,
  title,
  subtitle
}) => {
  const { user, updateUserProfile } = useSimplifiedAuth();
  const [phase, setPhase] = useState<CapturePhase>('idle');
  const [error, setError] = useState<string | null>(null);
  const [locationData, setLocationData] = useState<LocationData | null>(null);

  const config = CONTEXT_CONFIG[context];

  // Auto-attempt location detection if requested
  useEffect(() => {
    if (autoAttempt && phase === 'idle') {
      handleDetectLocation();
    }
  }, [autoAttempt]);

  // Reverse geocoding function
  const reverseGeocode = async (lat: number, lng: number): Promise<Partial<LocationData>> => {
    try {
      // Try multiple services for reliability
      const nominatimUrl = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=10&addressdetails=1`;
      
      const response = await fetch(nominatimUrl);
      const data = await response.json();
      
      if (data && data.address) {
        return {
          city: data.address.city || data.address.town || data.address.village || data.address.municipality,
          state: data.address.state || data.address.region,
          country: data.address.country,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        };
      }
    } catch (error) {
      console.warn('Reverse geocoding failed:', error);
    }
    
    return {
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };
  };

  // Main location detection function
  const handleDetectLocation = useCallback(async () => {
    setPhase('detecting');
    setError(null);

    try {
      // Check if geolocation is supported
      if (!navigator.geolocation) {
        throw new Error('Geolocation is not supported by this browser');
      }

      // Request high-accuracy position
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          resolve,
          reject,
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 300000 // 5 minutes
          }
        );
      });

      const { latitude, longitude, accuracy } = position.coords;
      
      // Reverse geocode to get address details
      const addressData = await reverseGeocode(latitude, longitude);
      
      // Determine location method based on accuracy
      let method: LocationData['method'] = 'gps_low';
      if (accuracy < 100) method = 'gps_high';
      else if (accuracy < 1000) method = 'gps_low';
      else method = 'network';

      const location: LocationData = {
        latitude,
        longitude,
        accuracy,
        method,
        timestamp: new Date().toISOString(),
        ...addressData
      };

      setLocationData(location);
      setPhase('success');

      // Update user profile
      if (updateUserProfile) {
        await updateUserProfile({ location });
      }

      // Callback to parent
      onLocationCaptured?.(location);

    } catch (error: any) {
      console.error('Location detection failed:', error);
      
      let errorMessage = 'Unable to detect location';
      if (error.code === 1) {
        errorMessage = 'Location access denied. You can still enter your city manually.';
      } else if (error.code === 2) {
        errorMessage = 'Location unavailable. Please check your connection.';
      } else if (error.code === 3) {
        errorMessage = 'Location request timed out. You can try again or enter manually.';
      }
      
      setError(errorMessage);
      setPhase('error');
    }
  }, [onLocationCaptured, updateUserProfile]);

  // Handle manual location entry
  const handleManualEntry = () => {
    setPhase('manual');
    // This would open a search/manual entry modal
    // For now, we'll just show a message
    setError('Manual location entry coming soon. Please try auto-detection or skip for now.');
  };

  // Handle skip
  const handleSkip = () => {
    onSkip?.();
  };

  // Render based on current phase
  const renderContent = () => {
    switch (phase) {
      case 'detecting':
        return (
          <>
            <LocationIcon $detecting>📡</LocationIcon>
            <Title>Detecting Your Location...</Title>
            <Subtitle>This will help us show you relevant satellite passes</Subtitle>
            <DetectingState>
              <div>🔍 Connecting to GPS satellites...</div>
            </DetectingState>
          </>
        );

      case 'success':
        return (
          <>
            <LocationIcon>✅</LocationIcon>
            <Title>Location Set Successfully!</Title>
            <SuccessState>
              Perfect! You'll now get personalized satellite predictions.
              {locationData && (
                <div className="location-details">
                  📍 {locationData.city}, {locationData.country}
                  <br />
                  🎯 Accuracy: {Math.round(locationData.accuracy)}m
                </div>
              )}
            </SuccessState>
          </>
        );

      case 'error':
        return (
          <>
            <LocationIcon>⚠️</LocationIcon>
            <Title>Location Detection Failed</Title>
            <ErrorMessage>{error}</ErrorMessage>
            <div style={{ marginTop: '20px' }}>
              <ActionButton $variant="primary" onClick={handleDetectLocation}>
                Try Again
              </ActionButton>
              <ActionButton $variant="secondary" onClick={handleManualEntry}>
                Enter Manually
              </ActionButton>
              {showSkipOption && (
                <ActionButton $variant="secondary" onClick={handleSkip}>
                  Skip for Now
                </ActionButton>
              )}
            </div>
          </>
        );

      default: // idle
        return (
          <>
            <LocationIcon>{config.icon}</LocationIcon>
            <Title>{title || config.title}</Title>
            <Subtitle>{subtitle || config.subtitle}</Subtitle>
            <div>
              <ActionButton $variant="primary" onClick={handleDetectLocation}>
                {config.primaryAction}
              </ActionButton>
              <ActionButton $variant="secondary" onClick={handleManualEntry}>
                {config.secondaryAction}
              </ActionButton>
              {showSkipOption && (
                <ActionButton $variant="secondary" onClick={handleSkip}>
                  {config.skipAction}
                </ActionButton>
              )}
            </div>
            <PrivacyNote>
              🔒 Your location is used only for satellite predictions and is stored securely. 
              You can change or remove it anytime in settings.
            </PrivacyNote>
          </>
        );
    }
  };

  return (
    <Container $variant={context}>
      {renderContent()}
    </Container>
  );
};

export default SmartLocationCapture;